import type { FC, ReactNode } from 'react'

import { cn, getInitials } from '@/lib/utils'
import Container from '@/components/landing/container'
import FounderVideo from '@/components/landing/founder-video'
import SectionHeading from '@/components/landing/section-heading'

import { LEADERSHIP } from '@/constants/landing'

const AVATAR_TONES: string[] = [
    'bg-primary text-primary-foreground',
    'bg-brand-teal-ink text-white',
    'bg-brand-orange text-brand-navy',
    'bg-secondary text-primary',
    'bg-accent text-brand-teal-ink'
]

const LeadershipSection: FC = (): ReactNode => {
    return (
        <section id='team' aria-labelledby='team-title' className='bg-card py-20 sm:py-28'>
            <Container>
                <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
                    <FounderVideo />

                    <div>
                        <SectionHeading
                            id='team-title'
                            eyebrow="Our story"
                            title="Built by people who have spent decades growing talent"
                            description="Skillkafe began in 2020 as EduKafe, offering graduate and postgraduate courses, and grew into job-ready skilling for learners in smaller cities across India."
                        />

                        <figure className='mt-9 border-l-4 border-brand-orange pl-6'>
                            <blockquote className='font-heading text-xl font-medium leading-relaxed text-foreground sm:text-2xl'>
                                <p>
                                    &ldquo;Together, we can aim to transform lives, careers, and communities!&rdquo;
                                </p>
                            </blockquote>

                            <figcaption className='mt-4 text-sm text-muted-foreground'>
                                <span className='font-semibold text-foreground'>Sukesh Dash</span>
                                , Founder and CEO
                            </figcaption>
                        </figure>
                    </div>
                </div>

                <ul className='mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-5'>
                    {LEADERSHIP.map((leader, index) => (
                        <li key={leader.name} className='reveal flex flex-col rounded-[1.5rem] border border-border bg-background p-6'>
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'grid size-14 place-items-center rounded-2xl font-heading text-lg font-semibold',
                                    AVATAR_TONES[index % AVATAR_TONES.length]
                                )}
                            >
                                {getInitials(leader.name)}
                            </span>

                            <h3 className='mt-5 text-lg font-semibold leading-snug text-foreground'>
                                {leader.name}
                            </h3>

                            <p className='mt-1 text-sm font-semibold text-brand-teal-ink'>
                                {leader.role}
                            </p>

                            <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                {leader.bio}
                            </p>
                        </li>
                    ))}
                </ul>
            </Container>
        </section>
    )
}

export default LeadershipSection
